import { apiGet, apiPost, apiPatch, apiDelete, getAuthHeaders, API_BASE } from '@/utils/api';
import type {
  SurveySection,
  SurveySettings,
  SurveyFilters,
  SurveyResultFilters,
  SurveyResultsSummary,
  ProfessorResult,
  MatiereResult,
  CommentResponse,
  ParticipationRow,
  PendingSurveyItem,
  SurveyFormResponse,
  SubmitSurveyRequest,
  SurveyActivationMode,
  SurveyQuestionType,
} from '@/types/course-evaluation.types';

// ==================== SECTIONS ====================

export async function getSurveySections(): Promise<SurveySection[]> {
  return apiGet<SurveySection[]>('/course-evaluation/sections');
}

export async function createSurveySection(data: {
  code: string;
  title: string;
  description?: string;
}): Promise<SurveySection> {
  return apiPost<SurveySection>('/course-evaluation/sections', data);
}

export async function updateSurveySection(
  id: string,
  data: { code?: string; title?: string; description?: string | null; isActive?: boolean }
): Promise<SurveySection> {
  return apiPatch<SurveySection>(`/course-evaluation/sections/${id}`, data);
}

export async function deleteSurveySection(id: string): Promise<void> {
  await apiDelete<{ success: boolean }>(`/course-evaluation/sections/${id}`);
}

export async function reorderSurveySections(sectionIds: string[]): Promise<void> {
  await apiPost<{ success: boolean }>('/course-evaluation/sections/reorder', { sectionIds });
}

export async function resetSurveyToDefaults(): Promise<void> {
  await apiPost<{ success: boolean }>('/course-evaluation/sections/reset-defaults');
}

// ==================== QUESTIONS ====================

export async function createSurveyQuestion(data: {
  sectionId: string;
  questionText: string;
  questionType?: SurveyQuestionType;
  isRequired?: boolean;
}): Promise<unknown> {
  return apiPost<unknown>('/course-evaluation/questions', data);
}

export async function updateSurveyQuestion(
  id: string,
  data: { questionText?: string; questionType?: SurveyQuestionType; isRequired?: boolean; isActive?: boolean }
): Promise<unknown> {
  return apiPatch<unknown>(`/course-evaluation/questions/${id}`, data);
}

export async function deleteSurveyQuestion(id: string): Promise<void> {
  await apiDelete<{ success: boolean }>(`/course-evaluation/questions/${id}`);
}

export async function reorderSurveyQuestions(sectionId: string, questionIds: string[]): Promise<void> {
  await apiPost<{ success: boolean }>('/course-evaluation/questions/reorder', { sectionId, questionIds });
}

// ==================== SETTINGS ====================

export async function getSurveySettings(): Promise<SurveySettings> {
  return apiGet<SurveySettings>('/course-evaluation/settings');
}

export async function updateSurveySettings(data: {
  activationMode?: SurveyActivationMode;
  thresholdPercentage?: number;
  fixedActivationDate?: string | null;
  daysBeforeSemesterEnd?: number | null;
  isActive?: boolean;
}): Promise<SurveySettings> {
  return apiPatch<SurveySettings>('/course-evaluation/settings', data);
}

// ==================== RESULTS ====================

export async function getSurveyFilters(): Promise<SurveyFilters> {
  return apiGet<SurveyFilters>('/course-evaluation/results/filters');
}

function buildFilterParams(filters: SurveyResultFilters): string {
  const params = new URLSearchParams();
  if (filters.academicYearId) params.append('academicYearId', filters.academicYearId);
  if (filters.semesterNumber) params.append('semesterNumber', String(filters.semesterNumber));
  if (filters.campusId) params.append('campusId', filters.campusId);
  if (filters.filiereId) params.append('filiereId', filters.filiereId);
  if (filters.niveau) params.append('niveau', String(filters.niveau));
  if (filters.groupeIds?.length) params.append('groupeIds', filters.groupeIds.join(','));
  if (filters.moduleElementIds?.length) params.append('moduleElementIds', filters.moduleElementIds.join(','));
  if (filters.professorIds?.length) params.append('professorIds', filters.professorIds.join(','));
  return params.toString();
}

export async function getSurveyResultsSummary(filters: SurveyResultFilters = {}): Promise<SurveyResultsSummary> {
  const params = buildFilterParams(filters);
  return apiGet<SurveyResultsSummary>(`/course-evaluation/results/summary?${params}`);
}

export async function getSurveyResultsByProfessor(filters: SurveyResultFilters = {}): Promise<ProfessorResult[]> {
  const params = buildFilterParams(filters);
  return apiGet<ProfessorResult[]>(`/course-evaluation/results/by-professor?${params}`);
}

export async function getSurveyResultsByMatiere(filters: SurveyResultFilters = {}): Promise<MatiereResult[]> {
  const params = buildFilterParams(filters);
  return apiGet<MatiereResult[]>(`/course-evaluation/results/by-matiere?${params}`);
}

export async function getSurveyComments(filters: SurveyResultFilters = {}): Promise<CommentResponse[]> {
  const params = buildFilterParams(filters);
  return apiGet<CommentResponse[]>(`/course-evaluation/results/comments?${params}`);
}

// ==================== PARTICIPATION ====================

export async function getSurveyParticipation(filters: SurveyResultFilters = {}): Promise<ParticipationRow[]> {
  const params = buildFilterParams(filters);
  return apiGet<ParticipationRow[]>(`/course-evaluation/participation?${params}`);
}

// ==================== EXPORTS ====================

export async function exportSurveyExcel(filters: SurveyResultFilters = {}): Promise<Blob> {
  const params = buildFilterParams(filters);
  const response = await fetch(`${API_BASE}/course-evaluation/export/excel?${params}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    throw new Error('Erreur lors de l\'export Excel');
  }
  return response.blob();
}

// ==================== STUDENT ENDPOINTS ====================

export async function getPendingSurveys(): Promise<PendingSurveyItem[]> {
  return apiGet<PendingSurveyItem[]>('/course-evaluation/student/pending');
}

export async function getSurveyForm(moduleElementId: string): Promise<SurveyFormResponse> {
  return apiGet<SurveyFormResponse>(`/course-evaluation/student/form/${moduleElementId}`);
}

export async function submitSurvey(data: SubmitSurveyRequest): Promise<void> {
  await apiPost<{ success: boolean; message: string }>('/course-evaluation/student/submit', data);
}
